import React from 'react';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '2rem',
          fontFamily: 'Inter, system-ui, sans-serif',
          maxWidth: '800px',
          margin: '2rem auto',
          background: '#FFFFFF',
          color: '#17181A',
          border: '1px solid #E1E0DB',
          borderRadius: '20px',
          boxShadow: '0 1px 0 rgba(23,24,26,0.04), 0 12px 32px -12px rgba(23,24,26,0.18)',
        }}>
          <h1 style={{ color: '#17181A', fontSize: '1.5rem', fontWeight: 600, marginBottom: '1rem' }}>
            Something went wrong. Reload to carry on.
          </h1>
          <div style={{
            background: '#EFEEE9',
            color: '#B4413A',
            fontFamily: 'ui-monospace, monospace',
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: '0.875rem',
          }}>
            <strong>Error:</strong> {this.state.error?.message}
          </div>
          {this.state.error?.stack && (
            <details style={{ marginBottom: '1rem' }}>
              <summary style={{ cursor: 'pointer', color: '#66655F' }}>Stack trace</summary>
              <pre style={{
                background: '#EFEEE9',
                padding: '1rem',
                borderRadius: '4px',
                overflow: 'auto',
                fontSize: '0.75rem',
                marginTop: '0.5rem',
              }}>
                {this.state.error.stack}
              </pre>
            </details>
          )}
          {this.state.errorInfo?.componentStack && (
            <details>
              <summary style={{ cursor: 'pointer', color: '#66655F' }}>Component stack</summary>
              <pre style={{
                background: '#EFEEE9',
                padding: '1rem',
                borderRadius: '4px',
                overflow: 'auto',
                fontSize: '0.75rem',
                marginTop: '0.5rem',
              }}>
                {this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              background: '#17181A',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '999px',
              cursor: 'pointer',
            }}
          >
            Reload the page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
